import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { IconButton, Skeleton, Typography } from "@mui/material";
import { ColorPreviewDisplay } from "components/dataDisplay/ColorPreviewDisplay";
import { OpacityDisplay } from "components/dataDisplay/OpacityDisplay";
import { usePreviewSpecification } from "hooks/specifications";
import { useIsDesktop, useIsMobile } from "hooks/use-breakpoint";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./MaterialShowcase.css";

function useVisibleCards() {
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();

  if (isDesktop) return 5;
  if (isMobile) return 2;
  return 3;
}

export interface TileImageRendererProps {
  material: ColoredMaterial;
  isPreviewOpen: boolean;
  onClosePreview: () => void;
  onOpenDetail: () => void;
}

export type TileImageRenderer = (props: TileImageRendererProps) => ReactNode;

/** Default image renderer — uses CIE Lab color swatch. Storybook-safe (no auth). */
const DefaultTileImage = ({
  material,
  onOpenDetail,
}: TileImageRendererProps) => {
  const previewColor = usePreviewSpecification(material);
  if (!previewColor) {
    return <Skeleton variant="rectangular" width="100%" height="100%" />;
  }
  return (
    <ColorPreviewDisplay
      onClick={onOpenDetail}
      width="100%"
      height="100%"
      preview={previewColor}
    />
  );
};

interface MaterialTileProps {
  material: ColoredMaterial;
  isLogin: boolean;
  renderImage?: TileImageRenderer;
}

const MaterialTile = ({
  material,
  isLogin,
  renderImage,
}: MaterialTileProps) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleTileClick = () => {
    // Card click is intentionally a no-op; only the preview button is interactive
  };

  const imageRenderer = renderImage ?? DefaultTileImage;

  return (
    <>
      <button type="button" className="material-tile" onClick={handleTileClick}>
        <div className="material-tile__image">
          {imageRenderer({
            material,
            isPreviewOpen,
            onClosePreview: () => setIsPreviewOpen(false),
            onOpenDetail: handleTileClick,
          })}
          {material.digitalSample.hasDigitalSample && (
            <IconButton
              size="small"
              className="material-tile__preview-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsPreviewOpen(true);
              }}
            >
              <VisibilityOutlined />
            </IconButton>
          )}
        </div>
        <div className="material-tile__info">
          <div className="material-tile__name-row">
            <Typography className="material-tile__name" noWrap>
              {material.material.publicGradeName}
            </Typography>
            <OpacityDisplay fontSize="small" value={material.color.opacity} />
          </div>
          <Typography className="material-tile__code" noWrap>
            {material.color.finalColorCode}
          </Typography>
        </div>
      </button>
    </>
  );
};

interface MaterialShowcaseViewProps {
  materials?: ColoredMaterial[];
  loading?: boolean;
  isLogin?: boolean;
  renderImage?: TileImageRenderer;
  autoSlide?: boolean;
}

/** Pure presentation component — no auth/API hooks. Safe for Storybook. */
export const MaterialShowcaseView = ({
  materials,
  loading: isLoading = false,
  isLogin = false,
  renderImage,
  autoSlide = true,
}: MaterialShowcaseViewProps) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const visibleCards = useVisibleCards();

  const totalItems = materials?.length ?? 5;
  const maxIndex = Math.max(0, totalItems - visibleCards);
  const needsSlider = totalItems > visibleCards;

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    if (!needsSlider || !autoSlide) return;
    const timer = setInterval(goToNext, 5000);
    return () => clearInterval(timer);
  }, [needsSlider, autoSlide, goToNext]);

  return (
    <div className="material-showcase">
      <Typography className="material-showcase__title">
        {t(
          "ldp_materials_title",
          "Thousands of materials. Your next one is here.",
        )}
      </Typography>
      <Typography className="material-showcase__subtitle">
        {t(
          "ldp_materials_subtitle",
          "Five of many. Register or login to see them all.",
        )}
      </Typography>
      <div className="material-showcase__viewport">
        <div className="material-showcase__track-wrapper">
          <div
            className="material-showcase__track"
            style={{
              transform: needsSlider
                ? `translateX(calc(-${currentIndex} * (calc((100% - ${(visibleCards - 1) * 16}px) / ${visibleCards} + 16px))))`
                : undefined,
            }}
          >
            {isLoading
              ? Array.from({ length: visibleCards }).map((_, i) => (
                  <Skeleton
                    key={`skeleton-${i}`}
                    variant="rounded"
                    className="material-showcase__skeleton"
                    sx={{ aspectRatio: "200 / 260" }}
                  />
                ))
              : materials?.map((material) => (
                  <MaterialTile
                    key={material.eljExperimentId}
                    material={material}
                    isLogin={isLogin}
                    renderImage={renderImage}
                  />
                ))}
          </div>
        </div>
      </div>
      {needsSlider && (
        <div className="material-showcase__dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              type="button"
              key={`dot-${i}`}
              onClick={() => setCurrentIndex(i)}
              className={`material-showcase__dot ${i === currentIndex ? "material-showcase__dot--active" : ""}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
